import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  useEstateWorkItemNote_CreateEstateWorkItemNoteMutation,
  useEstateWorkItemNote_GetEstateWorkItemNotesByEstateWorkItemIdQuery,
} from 'queries';
import * as yup from 'yup';

import { Button } from 'common/button';
import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import UserAvatar from 'common/user-avatar';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useIsImpersonating from 'utils/useIsImpersonating';

import AssignedTaskNote from './assigned-task-note';

const schema = yup.object().shape({
  noteText: yup.string().required('Required'),
});

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
    color: '#1C2541',
    fontSize: '14px',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '80vh',
  },
  notesInputContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  notesInput: {
    display: 'flex',
    alignItems: 'center',
  },
  recentNotesContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
  },
  submitContainer: {
    display: 'flex',
    justifyContent: 'right',
    marginTop: '16px',
  },
}));

type Props = {
  estateWorkItemId: string;
  portalId: string;
};

type NoteValues = {
  noteText: string;
};

const AssignedTaskNotes = ({ estateWorkItemId, portalId }: Props) => {
  const classes = useStyles();
  const workItemNotesBottomRef = useRef<HTMLDivElement>(null);

  const currentUser = useCurrentUser();
  const isImpersonating = useIsImpersonating();

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<NoteValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      noteText: '',
    },
  });

  const scrollToRef = () => {
    workItemNotesBottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const {
    data: estateWorkItemNotesData,
    loading: estateWorkItemNotesLoading,
    refetch: refetchEstateWorkItemNotes,
  } = useEstateWorkItemNote_GetEstateWorkItemNotesByEstateWorkItemIdQuery({
    variables: {
      id: estateWorkItemId,
      portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate work item notes: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [createEstateWorkItemNote, { loading: createEstateWorkItemNoteExecuting }] =
    useEstateWorkItemNote_CreateEstateWorkItemNoteMutation({
      onCompleted: () => {
        scrollToRef();
        refetchEstateWorkItemNotes();
        reset();
        showToast({
          message: 'Note saved!',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating estate work item note: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const onSubmit = (values: NoteValues) => {
    const userId = isImpersonating ? null : currentUser?.id;

    createEstateWorkItemNote({
      variables: {
        input: {
          estateWorkItemId,
          note: values.noteText,
          portalId: portalId,
          userId,
        },
      },
    });
  };

  if (estateWorkItemNotesLoading || !currentUser) {
    return <Spinner />;
  }

  const estateWorkItemNotes = estateWorkItemNotesData?.data || [];

  return (
    <div className={classes.container}>
      <Box className={classes.recentNotesContainer}>
        {estateWorkItemNotes.map((estateWorkItemNote) => (
          <AssignedTaskNote key={estateWorkItemNote.id} estateWorkItemNote={estateWorkItemNote} />
        ))}
        <div ref={workItemNotesBottomRef} />
      </Box>
      <form className={classes.notesInputContainer} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className={classes.notesInput}>
            <UserAvatar
              firstName={currentUser.firstName || ''}
              avatarUrl={currentUser.avatarUrl}
              size="md"
            />

            <Controller
              name="noteText"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField {...field} inputRef={ref} errorMessage={errors.noteText?.message} />
              )}
            />
          </div>
        </div>
        <div className={classes.submitContainer}>
          <Button type="submit" disabled={isSubmitting || createEstateWorkItemNoteExecuting}>
            Add Note
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AssignedTaskNotes;
